/**
 * Global Sentry middleware for TanStack Start.
 *
 * Two layers, both wired into `src/start.ts`:
 *
 *   - `sentryRequestMiddleware` runs around every server request (SSR, API
 *     route handlers, server function RPC). Catches anything the inner
 *     handler throws, reports it to Sentry, and re-throws so the framework's
 *     normal error response continues unchanged.
 *
 *   - `sentryFunctionMiddleware` runs around every `createServerFn` call
 *     and tags events as `runtime: server_function` so the function-vs-route
 *     split is filterable in Sentry.
 *
 * The reporting itself lives in `~/lib/server/sentry-report` behind
 * `createServerOnlyFn`: `.server()` callbacks are NOT compiled out of the
 * client bundle, so a plain `import()` here would ship `@sentry/cloudflare`
 * to the browser. `createServerOnlyFn` replaces its argument with a thrower
 * in the client build, severing that import edge entirely.
 *
 * Aliases: global error capture, sentry tanstack middleware.
 */

import { createMiddleware, createServerOnlyFn } from "@tanstack/react-start";

const importSentryReport = createServerOnlyFn(
  () => import("~/lib/server/sentry-report"),
);

export const sentryRequestMiddleware = createMiddleware().server(
  async ({ next }) => {
    try {
      return await next();
    } catch (err) {
      const { reportToSentry } = await importSentryReport();
      reportToSentry(err, "request");
      throw err;
    }
  },
);

export const sentryFunctionMiddleware = createMiddleware({
  type: "function",
}).server(async ({ next }) => {
  try {
    return await next();
  } catch (err) {
    const { reportToSentry } = await importSentryReport();
    reportToSentry(err, "server_function");
    throw err;
  }
});
